import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Gift, Users, Store, Truck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BottomNav from '@/components/BottomNav';

const ReferPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState({ friend_100: true, partner_150: true });
  const [loading, setLoading] = useState(true);

  // Fetch offer status
  useEffect(() => {
    const fetchOfferStatus = async () => {
      try {
        const response = await axios.get(`${API}/refer-offers/status`);
        const offerMap = {};
        (response.data.offers || []).forEach(offer => {
          offerMap[offer.id] = offer.is_active;
        });
        setOffers(offerMap);
      } catch (error) {
        console.error('Error fetching offer status:', error);
        // Default to active if API fails
        setOffers({ friend_100: true, partner_150: true });
      } finally {
        setLoading(false);
      }
    };
    fetchOfferStatus();
  }, []);

  // Check if Refer & Earn card should be active (at least one offer is ON)
  const isReferCardActive = offers.friend_100 !== false || offers.partner_150 !== false;
  const isFriend100Active = offers.friend_100 !== false;
  const isPartner150Active = offers.partner_150 !== false;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Refer & Earn</h1>
              <p className="text-green-100 text-sm">Share and earn rewards</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Section A: Refer & Earn */}
        <div className={`bg-white rounded-2xl shadow-lg p-6 transition-all ${!isReferCardActive ? 'opacity-60' : ''}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-3 rounded-full ${isReferCardActive ? 'bg-green-100' : 'bg-gray-100'}`}>
              <Gift className={`w-6 h-6 ${isReferCardActive ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <h2 className={`text-xl font-bold ${isReferCardActive ? 'text-gray-800' : 'text-gray-500'}`}>Refer & Earn</h2>
          </div>
          
          <div className="space-y-4">
            <div className={`flex items-start gap-3 p-4 rounded-xl transition-all ${isFriend100Active ? 'bg-green-50' : 'bg-gray-100 opacity-60'}`}>
              <Users className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isFriend100Active ? 'text-green-600' : 'text-gray-400'}`} />
              <div>
                <p className={isFriend100Active ? 'text-gray-700' : 'text-gray-500'}>Refer a friend and earn <span className={`font-bold ${isFriend100Active ? 'text-green-600' : 'text-gray-500'}`}>₹100</span> after their first order.</p>
                {!isFriend100Active && <p className="text-xs text-gray-400 mt-1">Currently unavailable</p>}
              </div>
            </div>
            
            <div className={`flex items-start gap-3 p-4 rounded-xl transition-all ${isPartner150Active ? 'bg-emerald-50' : 'bg-gray-100 opacity-60'}`}>
              <div className="flex gap-1 flex-shrink-0 mt-0.5">
                <Store className={`w-5 h-5 ${isPartner150Active ? 'text-emerald-600' : 'text-gray-400'}`} />
                <Truck className={`w-5 h-5 ${isPartner150Active ? 'text-emerald-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className={isPartner150Active ? 'text-gray-700' : 'text-gray-500'}>Refer a restaurant or a delivery partner and earn <span className={`font-bold ${isPartner150Active ? 'text-emerald-600' : 'text-gray-500'}`}>₹150</span> on their successful registration.</p>
                {!isPartner150Active && <p className="text-xs text-gray-400 mt-1">Currently unavailable</p>}
              </div>
            </div>
          </div>
          
          {!isReferCardActive && (
            <p className="text-sm text-gray-500 mt-4 font-medium">Currently unavailable</p>
          )}
          
          <Button 
            onClick={() => isReferCardActive && navigate('/referral-details')}
            disabled={!isReferCardActive}
            className={`mt-4 px-6 py-3 rounded-xl font-semibold ${
              isReferCardActive 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            data-testid="refer-start-btn"
          >
            Start
          </Button>
        </div>

        {/* Section B: Profit Sharing */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-lg p-6 border border-amber-200">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Join 1% Profit Sharing Partner of FoodMatrix</h2>
          <p className="text-gray-600 mb-4">Become a partner and share in our success!</p>
          
          <Button 
            onClick={() => navigate('/profit-sharing')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-3 rounded-xl font-semibold"
            data-testid="know-more-btn"
          >
            Know More
          </Button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ReferPage;
